import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  Text,
  Box,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from 'contexts/AuthContext';
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app from 'firebase.js';
import SubscriptionSelector from './SubscriptionSelector';

const db = getFirestore(app);
const SUBSCRIPTION_CHECK_KEY = 'blueprint_subscription_checked_session';

export default function SubscriptionChecker() {
  const { currentUser } = useAuth();
  const [agentsNeedingSubscription, setAgentsNeedingSubscription] = useState([]);
  const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const modalBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.700', 'secondaryGray.600');

  useEffect(() => {
    const checkAgentSubscriptions = async () => {
      // Check if we've already checked in this session
      if (sessionStorage.getItem(SUBSCRIPTION_CHECK_KEY)) {
        setLoading(false);
        return;
      }

      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Fetch all agents for the current user
        const agentsQuery = query(
          collection(db, 'agents'),
          where('admins', 'array-contains', currentUser.uid)
        );
        const snapshot = await getDocs(agentsQuery);
        const agents = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // Filter agents without active subscriptions
        const agentsWithoutSubscription = agents.filter((agent) => {
          const status = agent.subscriptionStatus;
          return !status || (status !== 'active' && status !== 'trialing');
        });

        if (agentsWithoutSubscription.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch subscription data for all agents needing subscription
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setLoading(false);
          return;
        }

        const token = await user.getIdToken();

        // Fetch subscriptions and calculate deletion dates for all agents
        const agentsWithDeletionDates = await Promise.all(
          agentsWithoutSubscription.map(async (agent) => {
            const getSubscriptionUrl = `https://us-central1-polletask-dev.cloudfunctions.net/billing-getSubscriptionHttp?agentId=${agent.id}`;

            let subscription = null;
            try {
              const response = await fetch(getSubscriptionUrl, {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
              const result = await response.json();

              // Handle both success:false and 404 responses
              if (result.success && result.subscription) {
                subscription = result.subscription;
              } else if (!result.success && result.error === 'Subscription not found') {
                // Expected case: agent has no subscription record
                console.log('No subscription found for agent:', agent.id);
                subscription = null;
              } else if (!result.success) {
                console.error('Error fetching subscription:', result.error);
                subscription = null;
              }
            } catch (err) {
              console.error('Network error fetching subscription:', err);
              subscription = null;
            }

            // Calculate deletion date
            const deletionDate = calculateDeletionDate(agent, subscription);

            return {
              agent,
              subscription,
              deletionDate,
            };
          })
        );

        // Filter to only include agents with future deletion dates
        const now = new Date();
        const validAgents = agentsWithDeletionDates.filter(
          (item) => item.deletionDate > now
        );

        if (validAgents.length > 0) {
          setAgentsNeedingSubscription(validAgents);
          setCurrentAgentIndex(0);
          setIsModalOpen(true);
        }
      } catch (error) {
        console.error('Error checking agent subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAgentSubscriptions();
  }, [currentUser]);

  const calculateDeletionDate = (agent, subscription) => {
    let startDate;

    // If subscription exists and is canceled, use the cancellation date
    if (subscription && subscription.status === 'canceled') {
      const canceledAt = subscription.metadata?.updatedAt;
      startDate = canceledAt ? new Date(canceledAt) : null;
    }

    // If no cancellation date, use agent creation date
    if (!startDate) {
      const createdAt = agent.metadata?.createdAt;
      if (createdAt?.toDate) {
        startDate = createdAt.toDate();
      } else if (createdAt) {
        startDate = new Date(createdAt);
      } else {
        // Fallback to current date if no creation date
        startDate = new Date();
      }
    }

    // Add 15 days to the start date
    const deadline = new Date(startDate);
    deadline.setDate(deadline.getDate() + 15);
    return deadline;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleClose = () => {
    // Mark this session as checked
    sessionStorage.setItem(SUBSCRIPTION_CHECK_KEY, 'true');
    setIsModalOpen(false);
  };

  const handleSubscriptionComplete = () => {
    toast({
      status: 'success',
      title: 'Subscription activated!',
      description: 'Your agent is now fully set up.',
      duration: 5000,
      isClosable: true,
    });
    
    // If there are more agents needing subscription, show the next one
    if (currentAgentIndex < agentsNeedingSubscription.length - 1) {
      setCurrentAgentIndex(currentAgentIndex + 1);
    } else {
      // All agents handled, mark session as checked and close
      sessionStorage.setItem(SUBSCRIPTION_CHECK_KEY, 'true');
      setIsModalOpen(false);
    }
  };

  if (loading || !isModalOpen || agentsNeedingSubscription.length === 0) {
    return null;
  }

  const currentAgentData = agentsNeedingSubscription[currentAgentIndex];
  if (!currentAgentData) {
    return null;
  }

  const { agent: agentNeedingSubscription, deletionDate } = currentAgentData;

  return (
    <Modal isOpen={isModalOpen} onClose={handleClose} isCentered size="4xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader textAlign="center" w="100%" color={textColor}>
          Action Required: Subscribe to Keep Your Agent
          <Text fontSize="sm" fontWeight="normal" color={textColorSecondary} mt="1">
            {agentNeedingSubscription.name} ({agentNeedingSubscription.email})
          </Text>
          {agentsNeedingSubscription.length > 1 && (
            <Text fontSize="xs" color={textColorSecondary} mt="2">
              Agent {currentAgentIndex + 1} of {agentsNeedingSubscription.length} needing subscription
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Subscription Required</AlertTitle>
                <AlertDescription>
                  <Text mb={2}>
                    Your agent <strong>{agentNeedingSubscription.name}</strong> does not have an active
                    subscription.
                  </Text>
                  <Text fontWeight="bold" color="orange.600">
                    Your agent and its data will be permanently removed on {formatDate(deletionDate)} if no
                    subscription is active.
                  </Text>
                  <Text mt={2} fontSize="sm">
                    We've sent reminder emails to help you stay on track. Subscribe now to continue using your
                    agent.
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>

            <SubscriptionSelector
              agentId={agentNeedingSubscription.id}
              onSubscriptionComplete={handleSubscriptionComplete}
              isEarlyAccess={true}
            />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

